import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import SearchIcon from "@mui/icons-material/Search";
import { TableRow, TableCell } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import Chart from "../../../components/Chart";
import IconButton from '../../../components/Buttons/SubjectiveIcons';
import ChartButton from "../../../components/Button/ChartButton";
import DateRangePicker from "../../../components/FormFilelds/DateRangePicker";
import { BASE_URL, getAxiosConfig } from "../../../components/interface/Config";
import TableComponent from "../../../components/Table/TableComponent";
import TablePaginationComponent from "../../../components/TablePaginationComponent";
import { format } from "date-fns";
import { ObjectiveResponse, RespirationRate } from "../../../components/interface/ObjectiveInterface";

const RespirationRateForm: React.FC<{ patientId: string }> = ({ patientId }) => {
  const [respirationRateRecords, setRespirationRateRecords] = useState<RespirationRate[]>([])
  const [totalRecords, setTotalRecords] = useState(0)
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(5)
  const [openDialog, setOpenDialog] = useState(false)
  const [editRecord, setEditRecord] = useState<RespirationRate | null>(null)
  const [showChart, setShowChart] = useState(false)
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState('')
  const [snackbarSeverity, setSnackbarSeverity] = useState<
    'success' | 'info' | 'warning' | 'error'
  >('success')
  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>({
    startDate: '',
    endDate: ''
  })
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [recordToDelete, setRecordToDelete] = useState<RespirationRate | null>(null)
  const showSnackbar = (message: string, severity: 'success' | 'info' | 'warning' | 'error') => {
    setSnackbarMessage(message)
    setSnackbarSeverity(severity)
    setSnackbarOpen(true)
  }

  useEffect(() => {
    fetchRespirationRateRecords(dateRange.startDate, dateRange.endDate);
  }, [page, rowsPerPage, searchTerm])

  const fetchRespirationRateRecords = async (startDate: string = '', endDate: string = '') => {
    setLoading(true);
    
    console.log('Fetching respiration rate records with parameters:', {
        startDate,
        endDate,
        limit: rowsPerPage,
        page: page + 1,
        user_id: patientId,
        slug: 'respiration',
        searchkey: searchTerm || undefined
    });

    try {
        const response = await axios.get<ObjectiveResponse>(`${BASE_URL}/resource/vitals`, {
            params: {
                limit: rowsPerPage,
                page: page + 1,
                from: startDate || '',
                to: endDate || '',
                order_by: 'details->date',
                dir: 2,
                user_id: patientId,
                slug: 'respiration',
                searchkey: searchTerm || undefined
            },
            headers: getAxiosConfig().headers
        });

        console.log('Response received:', response.data);

        const vitals = response.data.data?.vitals;
        const total = response.data.data?.pagination?.total;

        if (Array.isArray(vitals) && vitals.length > 0) {
            const mappedRespirationRate: RespirationRate[] = vitals.map((vital): RespirationRate => ({
                id: vital.id,
                user_id: vital.user_id || 0, // Assuming user_id is in the response
                consult_id: vital.consult_id || null,
                peripheral_id: vital.peripheral_id || null,
                slug: vital.slug || 'respiration', // Default slug
                details: {
                  date: format(new Date(vital.details.date), 'dd-MM-yyyy'),
                    time: vital.details.time,
                    range_code: vital.details.range_code,
                    respiration: vital.details.respiration,
                    respirationFlag: vital.details.respirationFlag,
                    respirationFlagColor: vital.details.respirationFlagColor,
                },
                freeze: vital.freeze ,
                document: vital.document || null,
            }));

            console.log('Mapped respiration rate:', mappedRespirationRate);
            setRespirationRateRecords(mappedRespirationRate);
            setTotalRecords(total || 0);
        } else {
            console.log('No respiration rate records found.');
            setRespirationRateRecords([]);
            setTotalRecords(0);
        }
    } catch (error) {
        console.error('Error fetching respiration rate records:', error);
    } finally {
        setLoading(false);
        console.log('Loading state set to false');
    }
};

  const handleAddClick = () => {
    setEditRecord(null)
    setOpenDialog(true)
  }

  const handleDialogClose = () => {
    setOpenDialog(false)
    fetchRespirationRateRecords(dateRange.startDate, dateRange.endDate)
  }

  const handleDialogSave = (newRecord: RespirationRate) => {
    try{
    if (editRecord) {
      setRespirationRateRecords((prev) => prev.map((record) => (record.id === editRecord.id ? newRecord : record)));
      showSnackbar('RespirationRate updated successfully!', 'success');
    } else {
      setRespirationRateRecords((prev) => [...prev, newRecord])
      showSnackbar('RespirationRate saved successfully!', 'success');
    }
  } catch (error) {
    console.error('Error saving the record:', error);
    showSnackbar('Failed to save the record.', 'error'); // Handle error
  } finally {
    setOpenDialog(false);
    fetchRespirationRateRecords(); // Refresh data after saving
  }
}

  const handleClearFilters = () => {
    setSearchTerm('')
    setDateRange({ startDate: '', endDate: '' })
    setPage(0)
    fetchRespirationRateRecords()
    setFiltersApplied(false);
  }



  const renderRow = (record: RespirationRate, index: number) => (
    <TableRow key={index}>
      <TableCell className="px-4 py-3 font-jost" sx={{ fontFamily: 'Jost, sans-serif' , fontSize: '1rem'  }}>{index + 1}</TableCell>
      <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif' , fontSize: '1rem'  }}>{(record.details!.date)}</TableCell>
      <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif' , fontSize: '1rem'  }}>{record.details!.time}</TableCell>
      <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif'  , fontSize: '1rem' }}>{record.details!.respiration}</TableCell>
      <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif' , fontSize: '1rem'  }}>
        <span
          style={{
            backgroundColor: record.details!.range_code,
            padding: '4px 8px',
            borderRadius: '8px',
            color: '#F9F9F9'
          }}
        >
          {record.details!.respirationFlag}
        </span>
      </TableCell>
    </TableRow>
  )

  const chartData = {
    labels: respirationRateRecords
        .map((record) => record.details?.date) // Use optional chaining to access date
        .filter((date): date is string => date !== undefined), // Filter out undefined values
    datasets: [
        {
            label: 'Respiration Rate (bpm)',
            data: respirationRateRecords
                .map((record) => record.details?.respiration) // Use optional chaining to access respiration
                .filter((respiration): respiration is number => respiration !== undefined), // Filter out undefined values
            borderColor: 'rgba(75, 192, 192, 1)',
            backgroundColor: 'rgba(75, 192, 192, 0.5)',
            borderWidth: 2
        }
    ]
};

const chartOptions = {
  responsive: true,
  animations: {
    tension: {
      easing: 'linear',
      from: 0.4, // Start tension
      to: 0.0,   // End tension
      // loop: true,
    },
  },
};

  return (
    <div>
      <div className="flex items-end justify-between w-full gap-12">
        <div className="flex flex-grow space-x-2 gap-2">
          <DateRangePicker
            startDateId={'BloodPressure_Start_Date'}
            endDateId={'BloodPressure_End_Date'}
            onChange={(dates: { startDate: string; endDate: string }) => {
              setDateRange(dates)
            }}
            value={dateRange}
          />
            <IconButton
                icon={<SearchIcon />}
                onClick={() => {
                  fetchRespirationRateRecords(dateRange.startDate, dateRange.endDate);
                  setFiltersApplied(true); // Mark filters as applied when search is clicked
                }}
              />

              {filtersApplied && (
                <IconButton
                  icon={<ClearIcon />}
                  onClick={handleClearFilters}
                />
              )}

          </div>
        
        <div className="flex space-x-4">
          <ChartButton
            text={showChart ? 'List' : 'Chart'}
            onClick={() => setShowChart((prev) => !prev)}
          />
         
        </div>
      </div>

      {loading ? (
        <div className="text-center my-4"></div>
      ) : (
        <>
          {showChart ? ( // Conditional rendering based on showChart
            <Chart data={chartData} options={chartOptions} />
          ) : (
         
            <div>
              <TableComponent
                    headers={['S.No', 'Date', 'Time', 'Rate', 'Result']}
                    data={respirationRateRecords}
                    renderRow={renderRow} error={''}    
                    />
                    </div>
                  )}
                </>
              )}
     <TablePaginationComponent
        count={totalRecords}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(_event, newPage) => {
          setPage(newPage);
        }}
        onRowsPerPageChange={(event) => {
          const newRowsPerPage = parseInt(event.target.value, 10);
          setRowsPerPage(newRowsPerPage);
          setPage(0);
        }}
      />
     
     </div>
  );
}

export default RespirationRateForm
